import { useState } from "react";
import { X, ArrowLeftRight, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";

/**
 * Iter 225 — Side-by-side snapshot vs current diff viewer.
 * Used by RFQ / Estimate / Contract detail pages when the user clicks a
 * Versions row. Supports optional `onRestore` to roll the document back.
 *
 * Props:
 *   version   — the snapshot row {version, snapshot, line_items, sub_items?, created_at}
 *   current   — the current live doc (same shape as `snapshot`)
 *   currentLines — the current line items array
 *   fields    — [{key, label, kind?}] to render for header fields
 *   lineFields — [{key, label, kind?}] to render per line item
 *   onRestore — async () => void, if provided a Restore button appears
 *   onClose   — () => void
 *   title     — dialog title
 */
export default function DocDiffDialog({ version, current, currentLines,
                                        fields, lineFields, onRestore, onClose,
                                        title = "Version diff" }) {
  const [restoring, setRestoring] = useState(false);
  const snap = version?.snapshot || {};
  const snapLines = version?.line_items || [];

  const runRestore = async () => {
    if (!onRestore) return;
    if (!window.confirm("Restore this version? The current state will be snapshotted as v" + ((current?.version || 1) + 1) + " so the restore itself is reversible.")) return;
    setRestoring(true);
    try {
      await onRestore();
      toast.success("Restored");
      onClose();
    } catch (e) { toast.error(apiErrorMessage(e, "Restore failed")); }
    finally { setRestoring(false); }
  };

  const changedField = (k) => {
    const a = snap?.[k];
    const b = current?.[k];
    return !equal(a, b);
  };

  return (
    <div className="fixed inset-0 z-[1200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="doc-diff-dialog">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-6xl max-h-[90vh] flex flex-col bg-background border border-border shadow-2xl">
        <div className="px-4 py-3 border-b border-border bg-muted/40 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <ArrowLeftRight className="w-4 h-4" />
            {title}
            <span className="ml-2 text-[10px] text-muted-foreground font-normal">v{version?.version} · {new Date(version?.created_at).toLocaleString()}</span>
          </div>
          <button type="button" onClick={onClose} className="p-1 text-muted-foreground hover:text-foreground" data-testid="diff-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div className="border border-border">
              <div className="px-3 py-2 bg-muted/40 border-b border-border font-semibold">Snapshot · v{version?.version}</div>
              <div className="p-3 space-y-1.5">
                {fields.map((f) => (
                  <FieldRow key={f.key} label={f.label} value={snap[f.key]} kind={f.kind} changed={changedField(f.key)} side="left" />
                ))}
              </div>
            </div>
            <div className="border border-primary">
              <div className="px-3 py-2 bg-primary/10 border-b border-primary font-semibold">Current · v{current?.version || 1}</div>
              <div className="p-3 space-y-1.5">
                {fields.map((f) => (
                  <FieldRow key={f.key} label={f.label} value={current?.[f.key]} kind={f.kind} changed={changedField(f.key)} side="right" />
                ))}
              </div>
            </div>
          </div>

          {lineFields && (
            <div className="border border-border">
              <div className="px-3 py-2 bg-muted/40 border-b border-border text-xs font-semibold uppercase tracking-wide">Line items</div>
              <LineItemsDiff
                snapLines={snapLines}
                currentLines={currentLines || []}
                fields={lineFields}
              />
            </div>
          )}
        </div>

        <div className="px-4 py-3 border-t border-border bg-muted/20 flex items-center justify-end gap-2 text-xs">
          <button type="button" onClick={onClose} className="px-3 py-1.5 border border-border hover:bg-muted" data-testid="diff-cancel">Close</button>
          {onRestore && (
            <button type="button" onClick={runRestore} disabled={restoring} className="px-3 py-1.5 bg-amber-600 text-white hover:opacity-90 disabled:opacity-50 flex items-center gap-1.5 font-medium" data-testid="diff-restore">
              <RotateCcw className="w-3.5 h-3.5" /> {restoring ? "Restoring…" : `Restore v${version?.version}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function equal(a, b) {
  if (a === b) return true;
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  if (typeof a !== typeof b) return false;
  if (typeof a === "object") {
    try { return JSON.stringify(a) === JSON.stringify(b); } catch { return false; }
  }
  return false;
}

function FieldRow({ label, value, kind, changed, side }) {
  const highlight = changed ? (side === "left" ? "bg-rose-50 border-rose-200" : "bg-emerald-50 border-emerald-200") : "";
  return (
    <div className={`px-2 py-1.5 border ${highlight || "border-transparent"}`}>
      <div className="text-[10px] uppercase text-muted-foreground tracking-wide">{label}</div>
      <div className="text-xs mt-0.5 whitespace-pre-wrap break-words font-mono">{formatValue(value, kind)}</div>
    </div>
  );
}

function formatValue(v, kind) {
  if (v === null || v === undefined || v === "") return "—";
  if (kind === "money") return "₹" + Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 });
  if (kind === "pct") return Number(v) + "%";
  if (kind === "date") return String(v).slice(0, 10);
  if (kind === "json" || typeof v === "object") return JSON.stringify(v, null, 2);
  return String(v);
}

function LineItemsDiff({ snapLines, currentLines, fields }) {
  const snapById = new Map(snapLines.map((l) => [l.id, l]));
  const curById = new Map(currentLines.map((l) => [l.id, l]));
  const allIds = new Set([...snapById.keys(), ...curById.keys()]);
  const rows = Array.from(allIds).map((id) => {
    const s = snapById.get(id);
    const c = curById.get(id);
    let kind = "unchanged";
    if (s && !c) kind = "removed";
    else if (!s && c) kind = "added";
    else if (s && c) {
      for (const f of fields) {
        if (!equal(s[f.key], c[f.key])) { kind = "changed"; break; }
      }
    }
    return { id, s, c, kind };
  });

  const badge = {
    added: "bg-emerald-100 text-emerald-800 border-emerald-300",
    removed: "bg-rose-100 text-rose-800 border-rose-300",
    changed: "bg-amber-100 text-amber-800 border-amber-300",
    unchanged: "bg-slate-100 text-slate-500 border-slate-300",
  };

  return (
    <div className="divide-y divide-border">
      {rows.map((r) => (
        <div key={r.id} className="p-2 text-xs" data-testid={`diff-line-${r.id}`}>
          <div className="flex items-center gap-2 mb-1">
            <span className={`px-1.5 py-0.5 text-[9px] border uppercase ${badge[r.kind]}`}>{r.kind}</span>
            <span className="font-mono text-[10px] text-muted-foreground">{r.id.slice(0, 8)}</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <LinePanel line={r.s} fields={fields} placeholder="(not in snapshot)" tone="rose" active={r.kind !== "unchanged" && r.kind !== "added"} />
            <LinePanel line={r.c} fields={fields} placeholder="(deleted)" tone="emerald" active={r.kind !== "unchanged" && r.kind !== "removed"} />
          </div>
        </div>
      ))}
      {rows.length === 0 && <div className="p-6 text-center text-muted-foreground">No line items.</div>}
    </div>
  );
}

function LinePanel({ line, fields, placeholder, tone, active }) {
  if (!line) return <div className={`p-2 border border-dashed text-muted-foreground italic bg-muted/20`}>{placeholder}</div>;
  return (
    <div className={`p-2 border ${active ? (tone === "rose" ? "border-rose-200 bg-rose-50/50" : "border-emerald-200 bg-emerald-50/50") : "border-border"}`}>
      {fields.map((f) => (
        <div key={f.key} className="grid grid-cols-[80px_1fr] gap-1 items-baseline text-[11px]">
          <div className="text-[10px] uppercase text-muted-foreground">{f.label}</div>
          <div className="font-mono truncate">{formatValue(line[f.key], f.kind)}</div>
        </div>
      ))}
    </div>
  );
}
